/* eslint-disable prefer-destructuring */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import CircularSlider from 'react-circular-slider-svg';

import { UserDO } from 'wdna-components';
import { RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import ContainerModal from 'wdna-components/lib/modals/generics/ContainerModal';
import { JarvisWidget } from 'wdna-components/lib';
import { messageError, messageWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { waste, sensor, RegisterInfoDO, StatisticsHashDO } from '../../../../sensors/requests';

interface Props {
  container: string;
  className: string;
  mainFilter: any;
}

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;
  showGraph: boolean;
  id?: number;
  data: any[];
  arrayData: any[];
  circleData?: StatisticsHashDO[];
  fire?: boolean;
  battery?: boolean;
  dataTemp?: number;
  weightAlarm?: boolean;
  weightMax?: number;
  weightCur?: number;
}

export default class Summary extends React.Component<Props, State> {
  private mounted: boolean;
  private buttons: TableButtonsConfig;

  public constructor(props: Props) {
    super(props);

    this.mounted = false;

    this.buttons = {
      // toolbar
      // actions
      toggleItem: true,
      graph: true,
    };

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,
      showGraph: false,
      data: [],
      arrayData: [],
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      const filter: any = this.props.mainFilter;

      let companyId: number | undefined;
      if (filter.company !== '' && filter.company !== '0') {
        companyId = filter.company;
      }
      const [wasteTable] = await Promise.all([
        waste<RegisterInfoDO[]>('weights', undefined, { type: this.props.container, companyId }) || []
      ]);
      const { arrayData } = this.state;
      if (wasteTable.length !== 0) {
        for (let i: number = 0; i < wasteTable.length; i++) {
          const data: RegisterInfoDO = wasteTable[i];
          arrayData.push({
            id: data.sensorId,
            company: data.companyName,
            name: data.sensorName,
            description: data.sensorDescription,
            type: data.values.containerType,
            model: data.values.containerModel,
            size: data.values.containerSize,
            currentWeight: data.values.weightCur,
            currentTemp: data.values.temperatureCur,
          });
        }
      } else {
        messageWarning('Warning', 'No data for this subtype container');
      }

      if (this.mounted) {
        this.setState({
          data: arrayData,
        });
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public onGraph = (value: any): void => {
    const rowId: number = value.id;
    this.getDashboardData(rowId).catch(() => { /* pass */ });
    this.dataAlerts(rowId).catch(() => { /* pass */ });
    this.setState({
      id: rowId,
      update: window.getStr(),
      showGraph: true,
    });
  };

  public onDescription = (): string | undefined => {
    const { data } = this.state;
    for (let i: number = 0; i < data.length; i++) {
      if (data[i].id === this.state.id) {
        return data[i].description;
      }
    }
  }

  public onClose = (): void => {
    this.setState({
      update: window.getStr(),
      showGraph: false,
    });
  };

  public sliderColor = (): string => {
    const subtype: string = this.props.container;
    if (subtype === 'SUW') return '#3A4749';
    if (subtype === 'FOR') return '#654321';
    if (subtype === 'PAP') return '#3895D3';
    if (subtype === 'PET') return '#F7DC10';
    if (subtype === 'GLA') return '#0B6623';
    if (subtype === 'PNG') return '#735902';
    if (subtype === 'CRK') return '#8C1F28';
    return '';
  };

  public percentage = (weight: number, maxWeight: number): string => {
    let percent: number = (weight / maxWeight) * 100;
    if (percent > 100) percent = 100;
    if (percent < 0) percent = 0;
    return `${percent.toFixed()}%`;
  };

  public getDashboardData = async (id: number): Promise<void> => {
    const filter: any = this.props.mainFilter;
    const { date1 } = filter.arrFilterDate[0];
    const { date2 } = filter.arrFilterDate[filter.arrFilterDate.length - 1];
    const from: string = date1;
    const to: string = date2;
    const sensorId: number | undefined = id;

    const { company, verified, active } = filter;
    const granularity: any = { '24h': 'DAY', '7d': 'WEEK', '30d': 'MONTH' };
    const group: string = granularity[filter.groupDateTime];

    const reqDashboard: Record<string, any> = { group, from, to, sensorId };
    const reqStatistics: Record<string, any> = { from, to, sensorId };
    if (company) {
      reqDashboard.companyId = company;
      reqStatistics.companyId = company;
    }

    const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
    const userType: string = (user && user.type) || 'cli';
    if (userType === 'cli') {
      reqDashboard.verified = true;
      reqStatistics.verified = true;
      reqDashboard.active = true;
      reqStatistics.active = true;
    } else {
      if (Number(verified) === 1) {
        reqDashboard.verified = true;
        reqStatistics.verified = true;
      }
      if (Number(active) === 1) {
        reqDashboard.active = true;
        reqStatistics.active = true;
      }
    }
    const circleData: StatisticsHashDO[] = await waste<StatisticsHashDO[]>('statistics', undefined, reqStatistics) || [];
    this.setState({
      circleData,
    },
    () => {
      this.forceUpdate();
    });
  };

  public dataAlerts = async (id: number): Promise<void> => {
    const filter: any = this.props.mainFilter;
    const companyId: string = filter.company;

    let req2: any = {
      typeSensorId: [11],
      register: [5, 6],
      companyId: [companyId],
      sensorId: id
    };

    let req3: any = {
      typeSensorId: [13],
      register: [9, 10],
      companyId: [companyId],
      sensorId: id
    };

    if (companyId === '' || companyId === '0') {
      req2 = {
        typeSensorId: [11],
        register: [5, 6],
        sensorId: id
      };
    }

    if (companyId === '' || companyId === '0') {
      req3 = {
        typeSensorId: [13],
        register: [9, 10],
        sensorId: id
      };
    }

    const req4: any = {
      sensorId: id,
    };

    const dataV1: RegisterInfoDO[] = await sensor<RegisterInfoDO[]>('register_lasts', undefined, req2) || [];
    const dataV2: RegisterInfoDO[] = await sensor<RegisterInfoDO[]>('register_lasts', undefined, req3) || [];
    const dataV3: RegisterInfoDO[] = await waste<RegisterInfoDO[]>('weights', undefined, req4) || [];
    let dataTemp: number | undefined;
    let fire: boolean = false;
    let dataBattery: number;
    let battery: boolean = false;
    const weight: number = dataV3[0].values.weightAlarm;
    const weightMax: number = dataV3[0].values.weightMax;
    const weightCur: number = dataV3[0].values.weightCur;
    let weightAlarm: boolean = false;
    if (weight === 1) weightAlarm = true;
    if (dataV1.length !== 0) {
      dataBattery = dataV1[0].values.d05;
      if (dataBattery < 3.6 || dataBattery > 4.2) {
        battery = true;
      }
      dataTemp = dataV1[0].values.d06.toFixed(1);
      if (dataTemp && dataTemp > 49) {
        fire = true;
      }
    }
    if (dataV2.length !== 0) {
      dataBattery = dataV2[0].values.d09;
      if (dataBattery < 3.6 || dataBattery > 4.2) {
        battery = true;
      }
      dataTemp = dataV2[0].values.d10.toFixed(1);
      if (dataTemp && dataTemp > 49) {
        fire = true;
      }
    }

    this.setState({
      dataTemp,
      fire,
      battery,
      weightAlarm,
      weightMax,
      weightCur,
    },
    () => {
      this.forceUpdate();
    });
  }

  public render(): JSX.Element {
    const { container, className } = this.props;
    const { loading, showGrid, showGraph, fire, battery, weightAlarm, weightMax, data, circleData } = this.state;
    let { weightCur } = this.state;

    if (weightCur && weightMax && weightCur > weightMax) weightCur = weightMax;
    let circleValue: number | undefined = weightCur;
    if (circleValue === undefined || circleValue < 0) circleValue = 0;
    let circleAngle: number = 323 - (circleValue * 286) / (weightMax || 1);
    if (circleAngle < 0) circleAngle = 46.2;
    const circleRadians: number = circleAngle * (Math.PI / 180);
    const styleRad: any = { left: `${34 + 58 * Math.cos(circleRadians)}px`, top: `${17 - 58 * Math.sin(circleRadians)}px` };

    const columnAlias: Record<string, string> = {
      id: 'ID',
      company: 'Company',
      name: 'Name',
      description: 'Description',
      type: 'Type',
      model: 'Model',
      size: 'Size',
      currentWeight: 'Weight',
      currentTemp: 'Temperature',

    };
    const columnsToHide: string[] = [
      'id',
    ];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'center' },
      company: { textAlign: 'left' },
      name: { textAlign: 'left' },
      code: { textAlign: 'left' },
      description: { textAlign: 'left' },
      model: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };
    const customColumnsDefs: Record<string, any> = {
      id: {
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        minWidth: 120,
      },
    };

    return (
      <div id="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data || []}
          alias={columnAlias}
          customCellStyles={columnStyles}
          hiddenCols={columnsToHide}
          buttons={this.buttons}
          customColumnsDefs={customColumnsDefs}
          loading={loading}
          rowDataChanged={this.rowDataChanged}
          onGraph={this.onGraph}
        />
        <ContainerModal
          fullScreenButton={false}
          show={showGraph}
          title="Info Sensors"
          onClose={this.onClose}
          modalClass="selector-modal"
          footerButtons={[{
            text: 'OK',
            className: 'btn btn-primary',
            onClick: this.onClose,
          }]}
        >
          <JarvisWidget
            editbutton={false}
            custombutton={false}
            togglebutton={false}
            fullscreenbutton={false}
            colorbutton={false}
            deletebutton={false}
            className="waste-main-dashboard"
          >
            <div className={`widget-body ${className} ${container}`}>
              <div className="container-alarms">
                {fire && (
                  <div className="alarm fire-alarm">
                    <div className="icon" />
                  </div>
                )}
                {weightAlarm && (
                  <div className="alarm weight-alarm">
                    <div className="icon" />
                  </div>
                )}
                {battery && (
                  <div className="alarm default-alarm">
                    <div className="icon" />
                  </div>
                )}
              </div>
              <header className="container-header">
                <h2>
                  <span>{this.onDescription()}</span>
                  <small>{container}</small>
                </h2>
              </header>
              <div className="radial_graph">
                <div className="weight_values">
                  <div className="container_percentage" style={styleRad}>
                    <b>{this.percentage((weightCur || 0), (weightMax || 1))}</b>
                  </div>
                  <div className="containers_total_weight">
                    {(circleData?.[0].data[0].x) || 0}
                  </div>
                  <div className="containers_day_weight">
                    {(circleData?.[0].data[1].x) || 0}
                    <span>&nbsp;&nbsp;&nbsp;&nbsp;Kg/ud</span>
                  </div>
                  <div className="container_unloads">
                    {(circleData?.[0].data[2].x) || 0}
                  </div>
                </div>
                <div className="connection_signs">
                  <div className="wave1" />
                  <div className="wave2" />
                </div>
                <div className="circular_slider">
                  <CircularSlider
                    handle1={{
                      value: (circleValue),
                      onChange: () => 100,
                    }}
                    minValue={0}
                    maxValue={weightMax || 1}
                    size={160}
                    startAngle={-53}
                    endAngle={233}
                    handleSize={15}
                    arcColor={this.sliderColor()}
                  />
                </div>
              </div>

              <div className="containers_stats big_containers" style={{ top: '205px', left: '68px' }}>
                <div style={{ top: '33px', left: '7px', position: 'absolute', fontSize: '17px' }}>
                  <b>{this.state.dataTemp}ºC</b>
                </div>
                <div className="containers_by_temp">
                  <div className="thermometer">
                    <div className="celsius_symbol">&#176;C</div>
                    <div className="thermometer_mask" />

                    <div className="temps_blocks">
                      <div className="hot_temp_block" style={{ height: '40%' }} />
                      <div className="warm_temp_block" style={{ height: '25%' }} />
                      <div className="cold_temp_block" style={{ height: '35%' }} />
                    </div>
                    <div className="lower_temps_blocks">
                      <span className="mask">
                        <span className="bars_group">
                          <div className="hot_temp_block" style={{ height: '40%' }} />
                          <div className="warm_temp_block" style={{ height: '25%' }} />
                          <div className="cold_temp_block" style={{ height: '35%' }} />
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </JarvisWidget>
        </ContainerModal>
      </div>
    );
  }
}
